// Shared dataset for the AI Risk Index — used by:
//  - NS4 homepage teaser (first 6 rows)
//  - /ai-risk-index standalone page (full table with search & filter)
//
// 24 sample roles spanning 11 departments. Designed for press / SEO.

export const RISK_LEVELS = {
  High: { color: '#EF4444', pillBg: '#FEE2E2', pillText: '#991B1B', dot: '#EF4444',
    meaning: 'Significant parts of this role will be automated within 3 years. Upskilling is urgent.' },
  Medium: { color: '#F59E0B', pillBg: '#FEF3C7', pillText: '#92400E', dot: '#F59E0B',
    meaning: 'AI will assist and change this role but not replace it. Adapt to stay ahead.' },
  Low: { color: '#1D9E75', pillBg: '#E1F5EE', pillText: '#0F6E56', dot: '#1D9E75',
    meaning: 'This role requires human judgment AI cannot replicate. Still evolving — stay current.' },
};

export const DEPARTMENTS = [
  'All', 'CSE / IT', 'AI / Data Science', 'Mechanical', 'Civil', 'ECE / EEE',
  'Finance', 'Marketing', 'HR', 'Operations', 'Healthcare', 'Legal', 'Management', 'Design',
];

export const RISK_ROLES = [
  { role: 'Software Engineer (Backend)', department: 'CSE / IT', risk: 'Medium',
    automates: 'Boilerplate code, test drafts, basic docs',
    staysHuman: 'Architecture decisions, debugging, production calls',
    stayRelevant: 'Master system design + an AI-agentic toolchain' },
  { role: 'Accountant', department: 'Finance', risk: 'High',
    automates: 'Bookkeeping, tax filing, basic audits',
    staysHuman: 'Advisory, client relationships, complex analysis',
    stayRelevant: 'Move into Forensic / Business Advisory' },
  { role: 'UI/UX Designer', department: 'Design', risk: 'Low',
    automates: 'Basic mockups, color palettes, copy suggestions',
    staysHuman: 'User research, empathy, design decisions',
    stayRelevant: 'Deepen research + service design skills' },
  { role: 'Data Analyst', department: 'AI / Data Science', risk: 'Medium',
    automates: 'Routine reports, basic dashboards',
    staysHuman: 'Business interpretation, stakeholder storytelling',
    stayRelevant: 'Add causal analysis + product analytics' },
  { role: 'Civil Engineer', department: 'Civil', risk: 'High',
    automates: 'Design drafts, structural calculations, documentation',
    staysHuman: 'Site judgment, contractor management, safety calls',
    stayRelevant: 'BIM specialist or Green/Sustainable construction' },
  { role: 'AI Product Manager', department: 'AI / Data Science', risk: 'Low',
    automates: 'Research compilation, meeting notes',
    staysHuman: 'Product vision, stakeholder alignment, prioritization',
    stayRelevant: 'Build AI-evaluation skills (evals, safety)' },
  { role: 'Frontend Engineer', department: 'CSE / IT', risk: 'Medium',
    automates: 'Boilerplate components, basic styling, simple unit tests',
    staysHuman: 'Performance work, accessibility, design partnership',
    stayRelevant: 'Master perf + accessibility + design systems' },
  { role: 'Content Writer', department: 'Marketing', risk: 'High',
    automates: 'Generic blog posts, SEO listicles, product descriptions',
    staysHuman: 'Brand voice, original journalism, complex narrative',
    stayRelevant: 'Brand strategy, audio/video scripts, journalism' },
  { role: 'Brand Strategist', department: 'Marketing', risk: 'Low',
    automates: 'Competitive lookups, deck drafts',
    staysHuman: 'Brand vision, cultural insight, narrative',
    stayRelevant: 'Sharpen consumer research + cultural reading' },
  { role: 'HR Manager', department: 'HR', risk: 'Medium',
    automates: 'Screening, scheduling, policy lookup',
    staysHuman: 'Culture, conflict resolution, leadership coaching',
    stayRelevant: 'People analytics + org design' },
  { role: 'Recruiter (Tech)', department: 'HR', risk: 'High',
    automates: 'Sourcing, initial screening, scheduling',
    staysHuman: 'Senior-IC + leadership closing, culture fit',
    stayRelevant: 'Move up the seniority ladder; learn calibrations' },
  { role: 'Mechanical Design Engineer', department: 'Mechanical', risk: 'Medium',
    automates: 'CAD drafting, basic FEA, repetitive design',
    staysHuman: 'Concept design, manufacturing judgement, on-site problem solving',
    stayRelevant: 'EV systems · Robotics & Mechatronics' },
  { role: 'Embedded Engineer', department: 'ECE / EEE', risk: 'Medium',
    automates: 'Boilerplate firmware, register maps',
    staysHuman: 'Real-time debugging, hardware-software bring-up',
    stayRelevant: 'Add Linux internals + safety-critical experience' },
  { role: 'Doctor (General Practitioner)', department: 'Healthcare', risk: 'Low',
    automates: 'Triage prompts, summarization, scheduling',
    staysHuman: 'Diagnosis judgment, patient trust, procedures',
    stayRelevant: 'Use AI as decision support, not replacement' },
  { role: 'Pharmacist', department: 'Healthcare', risk: 'Medium',
    automates: 'Prescription lookup, interaction checks',
    staysHuman: 'Counseling, dispensing accountability, clinical advice',
    stayRelevant: 'Clinical pharmacy / pharmacovigilance' },
  { role: 'Legal Researcher (Junior)', department: 'Legal', risk: 'High',
    automates: 'Case lookup, contract review drafts, summarisation',
    staysHuman: 'Court advocacy, negotiation, client trust',
    stayRelevant: 'Move into litigation strategy or specialised IP' },
  { role: 'Corporate Lawyer', department: 'Legal', risk: 'Medium',
    automates: 'Standard contracts, due-diligence first pass',
    staysHuman: 'Negotiation, client relationships, judgement calls',
    stayRelevant: 'Specialise in M&A / cross-border tax / IP' },
  { role: 'Operations Manager (Logistics)', department: 'Operations', risk: 'Medium',
    automates: 'Route planning, inventory forecasting',
    staysHuman: 'Vendor management, crisis decisions, on-ground ops',
    stayRelevant: 'Add supply-chain analytics + vendor leverage' },
  { role: 'Financial Analyst', department: 'Finance', risk: 'Medium',
    automates: 'Excel modelling, basic forecasting, ratio analysis',
    staysHuman: 'Deal judgment, IC memos, founder relationships',
    stayRelevant: 'Equity research, M&A strategy' },
  { role: 'Investment Banker (Analyst)', department: 'Finance', risk: 'Medium',
    automates: 'Slide formatting, basic valuation models',
    staysHuman: 'Client relationships, deal execution judgement',
    stayRelevant: 'Move into specialised sector coverage early' },
  { role: 'Operations Research / Supply Chain Lead', department: 'Operations', risk: 'Low',
    automates: 'Simulation runs, basic optimisation',
    staysHuman: 'Network design, partner negotiation, risk calls',
    stayRelevant: 'Sharpen optimisation + leadership skills' },
  { role: 'CEO / Founder', department: 'Management', risk: 'Low',
    automates: 'Routine analysis, research, scheduling',
    staysHuman: 'Vision, capital, culture, accountability',
    stayRelevant: 'Stay close to customers + capital markets' },
  { role: 'Mechanical Production Engineer', department: 'Mechanical', risk: 'High',
    automates: 'Process documentation, basic SPC, scheduling',
    staysHuman: 'Floor leadership, vendor calls, safety calls',
    stayRelevant: 'Move into manufacturing analytics / industry 4.0' },
  { role: 'Civil Site Supervisor', department: 'Civil', risk: 'Low',
    automates: 'Reporting, photo logs',
    staysHuman: 'Site supervision, labour management, safety calls',
    stayRelevant: 'Stay current with safety + green construction standards' },
];
